/**
 * Shell for the onboarding / CMM chat (sign-up flow).
 *
 * Web-only — this flow never runs in the native app. No app header is shown;
 * content fills the full viewport. On mobile web the layout is locked so the
 * iOS Safari keyboard doesn't shift the page.
 */

import Head from 'expo-router/head';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { View as RNView } from 'react-native';
import { GRADIENT_COLOR_TOP, View, YStack } from 'smoothie';

import { BackgroundGradient } from '../../../common/components/BackgroundGradient';
import { MobileWebChatContainerBottomContext } from './MobileWebChatLayoutContext';
import {
    MOBILE_WEB_FIXED_GRADIENT_STYLE,
    useMobileWebChatLock,
} from './useMobileWebChatLock';

interface OnboardingChatShellProps {
    children: ReactNode;
}

export const OnboardingChatShell = ({ children }: OnboardingChatShellProps) => {
    const {
        isMobileWebLocked,
        isLayoutReady,
        contentHeight,
        shellFixedRef,
        gradientLayerRef,
        initialViewportHeight,
        mobileWebContainerBottom,
    } = useMobileWebChatLock();

    return (
        <MobileWebChatContainerBottomContext.Provider
            value={mobileWebContainerBottom}
        >
            {/* Gradient layer is portalled to document.body so it sits behind
                the fixed content container without being clipped by it. */}
            {isMobileWebLocked &&
                typeof document !== 'undefined' &&
                createPortal(
                    <RNView
                        ref={gradientLayerRef}
                        style={{
                            ...MOBILE_WEB_FIXED_GRADIENT_STYLE,
                            height: initialViewportHeight,
                        }}
                    >
                        <BackgroundGradient />
                    </RNView>,
                    document.body,
                )}

            <View flex={1}>
                {isMobileWebLocked && (
                    <Head>
                        <meta name="theme-color" content={GRADIENT_COLOR_TOP} />
                    </Head>
                )}

                {isMobileWebLocked ? (
                    // Mobile web: pin the content at a fixed size so the keyboard
                    // opening doesn't push it off screen.
                    isLayoutReady ? (
                        <RNView
                            ref={shellFixedRef}
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: initialViewportHeight,
                                overflow: 'hidden',
                                zIndex: 1,
                            }}
                        >
                            <RNView
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: contentHeight,
                                    overflow: 'hidden',
                                    zIndex: 5,
                                }}
                            >
                                {children}
                            </RNView>
                        </RNView>
                    ) : null
                ) : (
                    // Desktop web: simple layout, gradient background, no header.
                    <>
                        <BackgroundGradient />
                        <YStack flex={1} minHeight={0}>
                            {children}
                        </YStack>
                    </>
                )}
            </View>
        </MobileWebChatContainerBottomContext.Provider>
    );
};
